import { BrokerType, FreedomType } from 'enums/shared/brokerType';

import tInvestmentsLogo from 'assets/img/broker/t-investments.png';
import tradernetLogo from 'assets/img/broker/freedom.svg';
import finamLogo from 'assets/img/broker/finam.svg';
import peLogo from 'assets/img/broker/pe.png';
import cryptoLogo from 'assets/img/broker/crypto.png';
import arbitrLogo from 'assets/img/broker/arbitr.png';

import tInvestmentsFullLogo from 'assets/img/broker/t-investments-full.png';

import tradernetFullLogo from 'assets/img/broker/tradernet-full.svg';
import freedomGlobal from 'assets/img/broker/freedom-global.svg';
import freedomTurlov from 'assets/img/broker/freedom-turlov.svg';
import freedomArmenia from 'assets/img/broker/freedom-armenia.svg';
import finamFullLogoRu from 'assets/img/broker/finam-full-ru.svg';
import finamFullLogoEn from 'assets/img/broker/finam-full-en.svg';

import binanceFullLogo from 'assets/img/broker/binance-full.svg';
import binanceLogo from 'assets/img/broker/binance.svg';
import ipoLogo from 'assets/img/broker/ipo.png';
import primeIpoLogo from 'assets/img/broker/prime_ipo.png';
import cryptoDepositLogo from 'assets/img/broker/crypto_deposit.png';
import agrCryptoLogo from 'assets/img/broker/agr_crypto.png';
import { useTranslation } from 'react-i18next';

interface Params {
  isFullLogo?: boolean;
}

const getLogoByAccountName = (accountName?: string): string | null => {
  const normalizedAccountName = accountName?.toLowerCase() ?? '';

  if (normalizedAccountName.includes('prime ipo')) {
    return primeIpoLogo;
  }

  if (
    normalizedAccountName.includes('agr') ||
    normalizedAccountName.includes('агр')
  ) {
    return agrCryptoLogo;
  }

  return null;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const useBrokerLogo = ({ isFullLogo = false }: Params = {}) => {
  const { i18n } = useTranslation();

  if (isFullLogo) {
    const logos: Record<BrokerType, string> = {
      [BrokerType.Tradernet]: tradernetFullLogo,
      [BrokerType.Freedom]: freedomGlobal,
      [BrokerType.Binance]: binanceFullLogo,
      [BrokerType.Finam]:
        i18n.language === 'ru' ? finamFullLogoRu : finamFullLogoEn,
      [BrokerType.Tinkoff]: tInvestmentsFullLogo,
      [BrokerType.PE]: peLogo,
      [BrokerType.Crypto]: cryptoLogo,
      [BrokerType.Arbitr]: arbitrLogo,
      [BrokerType.CryptoDeposit]: cryptoDepositLogo,
      [BrokerType.IPO]: ipoLogo,
      [BrokerType.NVB]: freedomGlobal,
      [BrokerType.PrimeIPO]: primeIpoLogo,
    };

    return ({
      brokerType,
      freedomType,
      accountName,
    }: {
      brokerType: BrokerType;
      freedomType?: FreedomType;
      accountName?: string;
    }) => {
      const logoByAccountName = getLogoByAccountName(accountName);

      if (logoByAccountName) {
        return logoByAccountName;
      }

      if (brokerType === BrokerType.Freedom) {
        if (freedomType === FreedomType.turlov) {
          return freedomTurlov;
        } else if (freedomType === FreedomType.global) {
          return freedomGlobal;
        } else {
          return freedomArmenia;
        }
      } else {
        return logos[brokerType];
      }
    };
  } else {
    const logos: Record<BrokerType, string> = {
      [BrokerType.Tradernet]: tradernetLogo,
      [BrokerType.Freedom]: tradernetLogo,
      [BrokerType.Tinkoff]: tInvestmentsLogo,
      [BrokerType.Finam]: finamLogo,
      [BrokerType.Binance]: binanceLogo,
      [BrokerType.PE]: peLogo,
      [BrokerType.Crypto]: cryptoLogo,
      [BrokerType.Arbitr]: arbitrLogo,
      [BrokerType.CryptoDeposit]: cryptoDepositLogo,
      [BrokerType.IPO]: ipoLogo,
      [BrokerType.NVB]: freedomGlobal,
      [BrokerType.PrimeIPO]: primeIpoLogo,
    };

    return ({
      brokerType,
      accountName,
    }: {
      brokerType: BrokerType;
      accountName?: string;
    }) => getLogoByAccountName(accountName) ?? logos[brokerType];
  }
};

export default useBrokerLogo;
